import { useState } from "react";
import { Link } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const LINKS = [
  { label: "Homepage", href: "/", testid: "nav-home-link", router: true },
  { label: "Origin Story", href: "/story", testid: "nav-story-link", router: true },
  { label: "The Marketplace", href: "/forum", testid: "nav-forum-link", router: true },
];

const starPoints = (cx, cy, r) => {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const angle = (-90 + i * 36) * (Math.PI / 180);
    const rad = i % 2 === 0 ? r : r * 0.42;
    pts.push(`${(cx + rad * Math.cos(angle)).toFixed(2)},${(cy + rad * Math.sin(angle)).toFixed(2)}`);
  }
  return pts.join(" ");
};

export const BetsyFlag = ({ className = "" }) => {
  const W = 190, H = 100, SH = H / 13, CW = 76, CH = SH * 7;
  const CX = CW / 2, CY = CH / 2, R = 22;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={className} role="img" aria-label="Betsy Ross flag">
      {Array.from({ length: 13 }).map((_, i) => (
        <rect key={i} x="0" y={i * SH} width={W} height={SH + 0.5} fill={i % 2 === 0 ? "#B22234" : "#F5F0E1"} />
      ))}
      <rect x="0" y="0" width={CW} height={CH} fill="#3C3B6E" />
      {Array.from({ length: 13 }).map((_, i) => {
        const angle = (-90 + i * (360 / 13)) * (Math.PI / 180);
        return (
          <polygon
            key={i}
            points={starPoints(CX + R * Math.cos(angle), CY + R * Math.sin(angle), 5.2)}
            fill="#F5F0E1"
          />
        );
      })}
    </svg>
  );
};

export const Nav = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <header data-testid="site-nav" className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#050505]/70 border-b border-white/10">
      <div className="flex items-center justify-between px-6 md:px-12 h-28 md:h-40">
        <Link to="/" data-testid="nav-brand-link" className="flex items-center gap-3 md:gap-5 group">
          <img src="/assets/logo.png" alt="Appalachian Guns LLC logo" data-testid="nav-logo" className="h-24 md:h-36 w-auto group-hover:scale-105 transition-transform duration-300" />
          <BetsyFlag
            data-testid="nav-betsy-flag"
            className="h-10 md:h-14 w-auto border border-white/20 opacity-90 [filter:sepia(0.35)_contrast(1.1)_brightness(0.85)] group-hover:opacity-100 transition-opacity duration-300"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) =>
            l.router ? (
              <Link key={l.label} to={l.href} data-testid={l.testid} className="text-sm uppercase tracking-widest text-white/60 hover:text-white transition-colors">
                {l.label}
              </Link>
            ) : (
              <a key={l.label} href={l.href} data-testid={l.testid} className="text-sm uppercase tracking-widest text-white/60 hover:text-white transition-colors">
                {l.label}
              </a>
            )
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin-link" className="text-sm uppercase tracking-widest text-primary hover:text-[#FF7733] transition-colors">
              Requests
            </Link>
          )}
          {user ? (
            <button data-testid="nav-logout-btn" onClick={logout} className="text-sm uppercase tracking-widest text-white/60 hover:text-white transition-colors">
              Sign Out
            </button>
          ) : (
            <Link to="/forum?signin=1" data-testid="nav-signin-link" className="text-sm uppercase tracking-widest text-white/60 hover:text-white transition-colors">
              Sign In
            </Link>
          )}
          <a
            href="/#book"
            data-testid="nav-book-btn"
            className="bg-primary text-white px-5 py-2.5 text-sm uppercase tracking-widest font-semibold hover:bg-[#FF7733] transition-colors"
          >
            Book a Visit
          </a>
        </nav>

        <button data-testid="nav-menu-toggle" className="md:hidden text-white" onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl px-6 py-6 flex flex-col gap-5">
          {LINKS.map((l) =>
            l.router ? (
              <Link key={l.label} to={l.href} data-testid={`${l.testid}-mobile`} onClick={() => setOpen(false)} className="text-sm uppercase tracking-widest text-white/70">
                {l.label}
              </Link>
            ) : (
              <a key={l.label} href={l.href} data-testid={`${l.testid}-mobile`} onClick={() => setOpen(false)} className="text-sm uppercase tracking-widest text-white/70">
                {l.label}
              </a>
            )
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin-link-mobile" onClick={() => setOpen(false)} className="text-sm uppercase tracking-widest text-primary">
              Requests
            </Link>
          )}
          <a href="/#book" data-testid="nav-book-btn-mobile" onClick={() => setOpen(false)} className="bg-primary text-white px-5 py-3 text-sm uppercase tracking-widest font-semibold text-center">
            Book a Visit
          </a>
        </div>
      )}
    </header>
  );
};
